import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShell } from './ShellContext';
import { NAV_ITEMS, isNavItemActive } from './navConfig';
import { ChevronLeftIcon } from './icons';
import Logo from './Logo';
import { SleeperAvatar } from './SleeperAvatar';
import { authAPI } from '../utils/api';

const SideNav = ({ collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const { leagues, draftsForFeature, sleeperUser } = useShell();
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkErr, setLinkErr] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  // Reset the inline link form whenever the account menu closes.
  useEffect(() => {
    if (!menuOpen) {
      setLinkOpen(false);
      setLinkName('');
      setLinkErr('');
    }
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/');
  };

  const handleLinkSleeper = async () => {
    const name = linkName.trim();
    if (!name) return;
    setLinkBusy(true);
    setLinkErr('');
    try {
      await authAPI.linkSleeper(name);
      await refreshUser();
      setLinkOpen(false);
      setLinkName('');
    } catch (err) {
      setLinkErr(err?.response?.data?.error || 'Could not link. Try again.');
    } finally {
      setLinkBusy(false);
    }
  };

  const sleeperName = user?.sleeperUsername || null;
  const leagueCount = leagues.length;

  return (
    <nav className="ft-sidenav">
      <div className="ft-brand">
        <Logo className="ft-logomark" />
        <div className="ft-brand-word">Fantasy Taco</div>
        <button className="ft-collapseicon" onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
          <ChevronLeftIcon />
        </button>
      </div>
      <div className="ft-navlist">
        {NAV_ITEMS.filter((item) => !item.requiresRole || user?.role === item.requiresRole).map((item) => {
          const active = isNavItemActive(item, location.pathname);
          const itemDrafts = draftsForFeature(item.featureKey);
          const resume = itemDrafts.length > 0 ? itemDrafts[0] : null;
          const ItemIcon = item.icon;
          return (
            <React.Fragment key={item.key}>
              <button
                className={`ft-navitem${active ? ' on' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <ItemIcon />
                <span className="ft-navlabel">{item.label}</span>
                {resume && !active ? <span className="ft-dot" /> : null}
              </button>
              {resume && !active && !collapsed ? (
                <button className="ft-navsub" onClick={() => navigate(resume.resumeUrl || item.path)}>
                  Resume — {resume.label}
                </button>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
      <div className="ft-navbottom" ref={menuRef}>
        <button
          className="ft-account"
          onClick={() => (user ? setMenuOpen((open) => !open) : navigate('/login'))}
        >
          <div className="ft-ava">
            {sleeperName ? (
              <SleeperAvatar id={sleeperUser?.avatar} name={sleeperName} size={32} />
            ) : (
              <img src="/logo-avatar.png" alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
            )}
          </div>
          <div className="ft-who">
            {user ? sleeperName || user.email : 'Sign in'}
            <span>
              {user
                ? leagueCount > 0
                  ? `${leagueCount} league${leagueCount === 1 ? '' : 's'}`
                  : 'Signed in'
                : 'Magic link, no password'}
            </span>
          </div>
        </button>
        {user && menuOpen && (
          <div className="ft-accmenu">
            <div className="ft-acchead">
              <b>{sleeperName || 'Account'}</b>
              {user?.email || 'Signed in with magic link'}
            </div>
            {sleeperName ? (
              <div className="ft-accitem" style={{ cursor: 'default' }}>
                <span>Sleeper connection</span>
                <span className="ft-accstatus">Linked</span>
              </div>
            ) : linkOpen ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLinkSleeper();
                }}
                style={{ padding: '6px 12px 10px' }}
              >
                <input
                  autoFocus
                  value={linkName}
                  onChange={(e) => {
                    setLinkName(e.target.value);
                    setLinkErr('');
                  }}
                  placeholder="Sleeper username"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: '13.5px',
                    border: '1px solid var(--ft-hairline)',
                    borderRadius: 9,
                    background: 'var(--ft-surface)',
                    outline: 'none',
                    marginBottom: 6,
                    boxSizing: 'border-box',
                  }}
                />
                {linkErr ? (
                  <div style={{ fontSize: 11, color: '#FF3B30', marginBottom: 6 }}>{linkErr}</div>
                ) : null}
                <button
                  type="submit"
                  disabled={linkBusy || !linkName.trim()}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: '#000',
                    borderRadius: 9,
                    cursor: linkBusy || !linkName.trim() ? 'default' : 'pointer',
                    opacity: linkBusy || !linkName.trim() ? 0.4 : 1,
                  }}
                >
                  {linkBusy ? 'Linking…' : 'Link Sleeper'}
                </button>
              </form>
            ) : (
              <button className="ft-accitem" onClick={() => setLinkOpen(true)}>
                <span>Sleeper connection</span>
                <span className="ft-accstatus">Not linked</span>
              </button>
            )}
            <button className="ft-accitem" onClick={handleLogout}>
              <span>Log out</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default SideNav;
